import { verdictFor } from './verdict';
import type { PlateReading, Verdict, VerdictComponent, VerdictField } from './types';

/**
 * judgeValue: the ONE way a value is judged against a unit's plate readings, for the browser (a typed value), the server's
 * stored verdicts (the record value) and the server's feedback re-judge. They all call this, so they cannot drift: the input
 * normalization (trimmed value, blank -> null; blank stored serial -> null) and the ATS downgrade live only here.
 */
export interface JudgeValueInput {
  component: VerdictComponent;
  field: VerdictField;
  /** The value judged. Trimmed here; blank, whitespace-only, null and undefined are all "no value" (a fill). */
  comparedValue: string | null | undefined;
  comparedSource: 'record' | 'human';
  /** The identity anchor: the unit's serial for this component. Trimmed here; blank means none. */
  storedSerial: string | null | undefined;
  unitHasAts2: boolean;
  readings: readonly PlateReading[];
}

/**
 * An ATS plate cannot say which of two switches it is, so an `ats` fill or conflict is only as good as the photo's label.
 * When the supporting photo is not labeled `ats`, the verdict is downgraded to unsure / wrong-component. Match, likely-typo,
 * and every other component are left alone. Pure.
 */
export function downgradeUnlabeledAts(v: Verdict, photoLabeled: boolean): Verdict {
  if (v.component !== 'ats' || photoLabeled || (v.kind !== 'fill' && v.kind !== 'conflict')) return v;
  return { ...v, kind: 'unsure', confidence: 'low', reason: 'wrong-component' };
}

const trimmedOrNull = (v: string | null | undefined): string | null => {
  const t = typeof v === 'string' ? v.trim() : '';
  return t.length > 0 ? t : null;
};

/** The verdict, and whether the photo it rests on carries a label for THIS component (a person, capture or unit pointer). */
export function judgeValueWithPhoto(input: JudgeValueInput): { verdict: Verdict; photoLabeled: boolean } {
  const raw = verdictFor({
    component: input.component,
    field: input.field,
    comparedValue: trimmedOrNull(input.comparedValue),
    comparedSource: input.comparedSource,
    storedSerial: trimmedOrNull(input.storedSerial),
    unitHasAts2: input.unitHasAts2,
    readings: input.readings,
  });
  const photo = raw.photoId ? input.readings.find((r) => r.photoId === raw.photoId) : undefined;
  const photoLabeled = photo?.labeledComponent === input.component;
  return { verdict: downgradeUnlabeledAts(raw, photoLabeled), photoLabeled };
}

export function judgeValue(input: JudgeValueInput): Verdict {
  return judgeValueWithPhoto(input).verdict;
}
